import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import type { QuestionDef } from '../../api/bindings/QuestionDef.ts';
import { useAnswers, useBuilderQuestions, usePutAnswer } from '../../api/hooks/useBuilder.ts';
import { usePlan } from '../../api/hooks/usePlans.ts';
import { inputClass } from '../../components/AuthCard.tsx';

function useDebouncedSave(planId: string) {
  const putAnswer = usePutAnswer(planId);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach(clearTimeout);
  }, []);
  return (key: string, answer: string | string[]) => {
    const existing = timers.current.get(key);
    if (existing) clearTimeout(existing);
    timers.current.set(
      key,
      setTimeout(() => putAnswer.mutate({ key, answer }), 600),
    );
  };
}

function Question({
  question,
  value,
  onChange,
}: {
  question: QuestionDef;
  value: string | string[] | undefined;
  onChange: (value: string | string[]) => void;
}) {
  const isList = question.input === 'string_list';
  const text = isList
    ? Array.isArray(value)
      ? value.join('\n')
      : ''
    : typeof value === 'string'
      ? value
      : '';

  return (
    <div className="mb-6">
      <label className="block">
        <span className="mb-1 block font-medium text-gray-800">{question.prompt}</span>
        {question.help && <span className="mb-2 block text-sm text-gray-500">{question.help}</span>}
        {question.input === 'text' ? (
          <input
            className={inputClass}
            value={text}
            onChange={(e) => onChange(e.target.value)}
          />
        ) : (
          <textarea
            className={`${inputClass} min-h-24`}
            value={text}
            onChange={(e) => onChange(isList ? e.target.value.split('\n') : e.target.value)}
          />
        )}
      </label>
    </div>
  );
}

export default function BuilderWizard() {
  const { planId } = useParams<{ planId: string }>();
  const plan = usePlan(planId);
  const questions = useBuilderQuestions();
  const answers = useAnswers(planId);
  const save = useDebouncedSave(planId ?? '');
  const [step, setStep] = useState(0);
  // local mirror so typing is instant; server cache updated optimistically on save
  const [local, setLocal] = useState<Record<string, string | string[]>>({});

  const groups = questions.data?.groups ?? [];
  const answered = useMemo(() => {
    const merged: Record<string, unknown> = { ...(answers.data?.answers ?? {}), ...local };
    return (key: string) => {
      const v = merged[key];
      return Array.isArray(v) ? v.some((s) => String(s).trim()) : Boolean(String(v ?? '').trim());
    };
  }, [answers.data, local]);

  if (plan.isPending || questions.isPending || answers.isPending) {
    return <main className="p-8 text-center text-sm text-gray-400">Loading…</main>;
  }
  if (plan.isError || questions.isError || answers.isError) {
    return <main className="p-8 text-center text-sm text-red-600">Could not load the builder.</main>;
  }

  const group = groups[step];
  const progress = groups.map((g) => ({
    key: g.key,
    title: g.title,
    done: g.questions.filter((q) => answered(q.key)).length,
    total: g.questions.length,
  }));

  return (
    <main className="mx-auto max-w-3xl p-8">
      <header className="mb-6">
        <Link to={`/plans/${planId}/edit`} className="text-sm text-blue-600 hover:underline">
          ← Back to editor
        </Link>
        <h1 className="text-2xl font-bold tracking-tight">{plan.data.plan.title}</h1>
        <p className="text-sm text-gray-500">
          Answer what you can — every answer improves the AI drafts. Everything autosaves.
        </p>
      </header>

      <nav className="mb-8 flex flex-wrap gap-2">
        {progress.map((p, i) => (
          <button
            key={p.key}
            onClick={() => setStep(i)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium ${
              i === step ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {p.title}
            <span className={`ml-2 text-xs ${i === step ? 'text-blue-200' : 'text-gray-400'}`}>
              {p.done}/{p.total}
            </span>
          </button>
        ))}
      </nav>

      <section className="rounded-xl border border-gray-200 bg-white p-6">
        <h2 className="mb-6 text-lg font-semibold text-gray-800">{group.title}</h2>
        {group.questions.map((q) => (
          <Question
            key={q.key}
            question={q}
            value={local[q.key] ?? (answers.data.answers[q.key] as string | string[] | undefined)}
            onChange={(value) => {
              setLocal((prev) => ({ ...prev, [q.key]: value }));
              save(
                q.key,
                Array.isArray(value) ? value.map((s) => s.trim()).filter(Boolean) : value,
              );
            }}
          />
        ))}
      </section>

      <div className="mt-6 flex justify-between">
        <button
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          disabled={step === 0}
          onClick={() => setStep((s) => s - 1)}
        >
          ← Previous
        </button>
        {step < groups.length - 1 ? (
          <button
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            onClick={() => setStep((s) => s + 1)}
          >
            Next →
          </button>
        ) : (
          <Link
            to={`/plans/${planId}/edit`}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          >
            Done — go draft the plan
          </Link>
        )}
      </div>
    </main>
  );
}
